import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmerReducer } from "use-immer";

import { IgTab, IgTabPanel, IgTabs, IgTypography } from "@intelligems/ig-theme";
import { Box, Drawer, Grid } from "@mui/material";
import { CustomerTab } from "@src/features/admin/pages/overview/components/Drawers/SubscriptionDrawer/CustomerTab/CustomerTab";
import { PhaseSettingsTab } from "@src/features/admin/pages/overview/components/Drawers/SubscriptionDrawer/PhaseSettingsTab/PhaseSettingsTab";
import { PricesTab } from "@src/features/admin/pages/overview/components/Drawers/SubscriptionDrawer/PricesTab/PricesTab";
import { ProductsTab } from "@src/features/admin/pages/overview/components/Drawers/SubscriptionDrawer/ProductsTab/ProductsTab";
import { SettingsSection } from "@src/features/admin/pages/overview/components/Drawers/SubscriptionDrawer/SettingsSection/SettingsSection";
import {
  initialCreateSubscriptionReducerState,
  SubscriptionReducer,
} from "@src/features/admin/pages/overview/components/Drawers/SubscriptionDrawer/subscriptionReducer";
import { closeDrawer, getDrawer } from "@src/store/slices/drawers";

export const CreateSubscriptionDrawer = () => {
  const drawer = useSelector(getDrawer("createSubscription"));
  const [payload, dispatchPayload] = useImmerReducer(
    SubscriptionReducer,
    initialCreateSubscriptionReducerState
  );

  const dispatch = useDispatch();

  const handleClose = () => {
    dispatch(closeDrawer("createSubscription"));
  };

  useEffect(() => {
    if (drawer !== undefined) {
      dispatchPayload({
        type: "setOrgs",
        value: drawer.orgs,
      });
      dispatchPayload({
        type: "setAgencyId",
        value: drawer.agencyId,
      });
      dispatchPayload({
        type: "setUsages",
        value: drawer.usages,
      });
    } else {
      dispatchPayload({ type: "reset" });
    }
  }, [drawer]);

  if (
    drawer !== undefined &&
    (!payload.orgs?.length || payload.phaseIndex === -1)
  ) {
    return null;
  }

  return (
    <Drawer
      anchor="right"
      open={drawer !== undefined}
      onClose={() => handleClose()}
      sx={{
        zIndex: 1600,
        "& .MuiDrawer-paper": {
          borderRadius: "0px",
          padding: "20px",
          paddingLeft: "40px",
          paddingRight: "40px",
        },
      }}
    >
      <Grid direction={"column"} container sx={{ width: 700 }} rowSpacing={4}>
        <Grid item>
          <IgTypography variant={"h2"} fontWeight={600}>
            Create Stripe Subscription
          </IgTypography>
        </Grid>
        <Box>
          <IgTabs
            underline={true}
            value={payload.tabIndex}
            onChange={(_e, value: number) => {
              dispatchPayload({ type: "setTabIndex", value });
            }}
          >
            <IgTab label="Customer" />
            <IgTab label="Products" disabled={!payload.customer?.name} />
            <IgTab
              label="Prices"
              disabled={
                !payload.customer?.name ||
                !payload.phases[payload.phaseIndex].items.length
              }
            />
            <IgTab
              label="Phase Settings"
              disabled={
                !payload.customer?.name ||
                !payload.phases[payload.phaseIndex].items.length
              }
            />
            <IgTab
              label="Settings"
              disabled={
                !payload.customer?.name ||
                !payload.phases[payload.phaseIndex].items.length
              }
            />
          </IgTabs>
        </Box>
        <IgTabPanel index={0} value={payload.tabIndex}>
          <CustomerTab
            index={0}
            payload={payload}
            dispatchPayload={dispatchPayload}
          />
        </IgTabPanel>
        <IgTabPanel index={1} value={payload.tabIndex}>
          <ProductsTab
            index={1}
            payload={payload}
            dispatchPayload={dispatchPayload}
          />
        </IgTabPanel>
        <IgTabPanel index={2} value={payload.tabIndex}>
          {payload.phases[payload.phaseIndex].items.length ? (
            <PricesTab
              index={2}
              payload={payload}
              dispatchPayload={dispatchPayload}
            />
          ) : null}
        </IgTabPanel>
        <IgTabPanel index={3} value={payload.tabIndex}>
          {payload.phases[payload.phaseIndex].items.length ? (
            <PhaseSettingsTab
              index={3}
              payload={payload}
              dispatchPayload={dispatchPayload}
            />
          ) : null}
        </IgTabPanel>
        <IgTabPanel index={4} value={payload.tabIndex}>
          {payload.customer &&
          payload.phases[payload.phaseIndex].items.length ? (
            <SettingsSection
              payload={payload}
              dispatchPayload={dispatchPayload}
            />
          ) : null}
        </IgTabPanel>
      </Grid>
    </Drawer>
  );
};
